import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

import { Snackbar, Alert, LinearProgress, Box } from '@mui/material';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info',
        duration: 3000,
    });

    const [progress, setProgress] = useState(0);

    const showNotification = useCallback((message, severity = 'info', delay = 3) => {
        setNotification({
            open: true,
            message,
            severity,
            duration: delay * 1000,
        });
        setProgress(0);
    }, []);

    useEffect(() => {
        if (!notification.open) return;

        const interval = 100;
        const totalTicks = notification.duration / interval;
        let ticks = 0;

        const timer = setInterval(() => {
            ticks++;
            setProgress((ticks / totalTicks) * 100);
            if (ticks >= totalTicks) clearInterval(timer);
        }, interval);

        return () => clearInterval(timer);
    }, [notification]);

    const handleClose = () => setNotification((prev) => ({ ...prev, open: false }));

    const getColor = (severity) => {
        switch (severity) {
            case 'success':
                return '#1976d2'; // azul
            case 'error':
                return '#d32f2f'; // rojo fuerte
            case 'warning':
                return '#ed6c02'; // naranja
            default:
                return '#333'; // gris
        }
    };

    return (
        <NotificationContext.Provider value={showNotification}>
            {children}
            <Snackbar
                open={notification.open}
                autoHideDuration={notification.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: '100%' }}>
                    <Alert
                        onClose={handleClose}
                        severity={notification.severity}
                        sx={{
                            width: '100%',
                            bgcolor: getColor(notification.severity),
                            color: '#fff',
                            '& .MuiAlert-icon': { color: '#fff' },
                        }}
                        variant="filled"
                    >
                        {notification.message}
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                mt: 1,
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#fff',
                                },
                            }}
                        />
                    </Alert>
                </Box>
            </Snackbar>
        </NotificationContext.Provider>
    );
};

// Hooks y helpers
export const useNotify = () => useContext(NotificationContext);

let notify = null;

export const NotificationInitializer = () => {
    notify = useContext(NotificationContext);
    return null;
};

export const notifySuccess = (message, delay = 3) => {
    notify?.(message, 'success', delay);
};

export const notifyError = (message, delay = 3) => {
    notify?.(message, 'error', delay);
};

export const notifyWarning = (message, delay = 3) => {
    notify?.(message, 'warning', delay);
};
